import { useErpProviders } from '@euler/app/flows/erp/functions/useErpProviders';
import { showErpToast } from '@euler/app/flows/erp/utils/showErpToast';
import { Button } from '@euler/components/Button';
import { Colors, StatusColors } from '@euler/components/Colors';
import {
  DateTimePicker,
  useDateTimePicker,
} from '@euler/components/DateTimePicker';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import {
  ErpSyncDirection,
  ErpSyncOrigin,
  ErpSyncResult,
  OrderSourceType,
} from '@euler/model/enum';
import { useServiceFactory } from '@euler/services/factory';
import { useNavigation, useRoute } from '@react-navigation/native';
import dayjs from 'dayjs';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

export const ErpOrderSyncScreen = wrapNavigatorScreen(
  () => {
    return <Content />;
  },
  {
    title: '订单同步',
  },
);

type SyncStatusMap = Record<string, { result: string; error?: string }>;

type TabSnapshot = {
  orders: any[];
  syncedOrderIds: Set<string>;
  expandedOrderIds: Set<string>;
  inboundSyncStatusMap: SyncStatusMap;
  outboundSyncStatusMap: SyncStatusMap;
  hasFetched: boolean;
  lastQueryKey: string;
  startDate: Date;
  endDate: Date;
};

type ScreenStateSnapshot = {
  activeTab: 'toZhiche' | 'toErp';
  tabSnapshots: Record<string, TabSnapshot>;
};

const erpOrderSyncScreenCache: Record<string, ScreenStateSnapshot> = {};

const Content = memo(() => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const { getProviderName } = useErpProviders();
  const providerId = route.params?.providerId;
  // Post-login calls are addressed by integration, not by provider; the
  // provider id stays for display, cache keys and sync-log fields.
  const integrationId = route.params?.integrationId;
  const providerName = getProviderName(providerId, '当前服务商');
  const { storeErpIntegrationService, orderService } = useServiceFactory();
  const screenCacheKey = `erp-order-sync:${providerId || 'unknown'}`;

  const [activeTab, setActiveTab] = useState<'toZhiche' | 'toErp'>('toZhiche');
  const [fetching, setFetching] = useState(false);
  const [syncingOrderId, setSyncingOrderId] = useState<string | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [syncedOrderIds, setSyncedOrderIds] = useState<Set<string>>(new Set());
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(
    new Set(),
  );
  const [employeeMappings, setEmployeeMappings] = useState<any[]>([]);
  const [erpEmployees, setErpEmployees] = useState<any[]>([]);
  const [inboundSyncStatusMap, setInboundSyncStatusMap] = useState<
    Record<string, { result: string; error?: string }>
  >({});
  const [outboundSyncStatusMap, setOutboundSyncStatusMap] = useState<
    Record<string, { result: string; error?: string }>
  >({});
  const [hasFetched, setHasFetched] = useState(false);
  const [lastQueryKey, setLastQueryKey] = useState('');

  // Date filter state
  const [startDate, setStartDate] = useState<Date>(
    dayjs().subtract(7, 'day').toDate(),
  );
  const [endDate, setEndDate] = useState<Date>(new Date());

  const [datePickerType, setDatePickerType] = useState<'start' | 'end'>(
    'start',
  );
  const [showPicker, showDateTimePicker, hideDateTimePicker] =
    useDateTimePicker();
  const tabSnapshotsRef = useRef<Record<string, TabSnapshot>>(
    erpOrderSyncScreenCache[screenCacheKey]?.tabSnapshots || {},
  );

  useEffect(() => {
    navigation.setOptions({
      title: `${providerName}订单同步`,
    });
  }, [navigation, providerName]);

  const currentQueryKey = useMemo(
    () =>
      [
        activeTab,
        dayjs(startDate).format('YYYY-MM-DD'),
        dayjs(endDate).format('YYYY-MM-DD'),
      ].join('|'),
    [activeTab, endDate, startDate],
  );

  const hasPendingFilters = hasFetched && currentQueryKey !== lastQueryKey;

  const getSnapshotKey = useCallback(
    (tab: 'toZhiche' | 'toErp') => `${providerId || 'unknown'}:${tab}`,
    [providerId],
  );

  const buildTabSnapshot = useCallback(
    (overrides?: Partial<TabSnapshot>): TabSnapshot => {
      const effectiveStartDate = overrides?.startDate ?? startDate;
      const effectiveEndDate = overrides?.endDate ?? endDate;
      return {
        orders: overrides?.orders ?? orders,
        syncedOrderIds: new Set(overrides?.syncedOrderIds ?? syncedOrderIds),
        expandedOrderIds: new Set(
          overrides?.expandedOrderIds ?? expandedOrderIds,
        ),
        inboundSyncStatusMap: {
          ...(overrides?.inboundSyncStatusMap ?? inboundSyncStatusMap),
        },
        outboundSyncStatusMap: {
          ...(overrides?.outboundSyncStatusMap ?? outboundSyncStatusMap),
        },
        hasFetched: overrides?.hasFetched ?? hasFetched,
        lastQueryKey: overrides?.lastQueryKey ?? lastQueryKey,
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
      };
    },
    [
      endDate,
      expandedOrderIds,
      hasFetched,
      inboundSyncStatusMap,
      lastQueryKey,
      orders,
      outboundSyncStatusMap,
      startDate,
      syncedOrderIds,
    ],
  );

  const saveTabSnapshot = useCallback(
    (tab: 'toZhiche' | 'toErp', overrides?: Partial<TabSnapshot>) => {
      tabSnapshotsRef.current[getSnapshotKey(tab)] =
        buildTabSnapshot(overrides);
      erpOrderSyncScreenCache[screenCacheKey] = {
        activeTab,
        tabSnapshots: { ...tabSnapshotsRef.current },
      };
    },
    [activeTab, buildTabSnapshot, getSnapshotKey, screenCacheKey],
  );

  const resetTabState = useCallback(
    (overrides?: { startDate?: Date; endDate?: Date }) => {
      setOrders([]);
      setSyncedOrderIds(new Set());
      setExpandedOrderIds(new Set());
      setInboundSyncStatusMap({});
      setOutboundSyncStatusMap({});
      setHasFetched(false);
      setLastQueryKey('');
      if (overrides?.startDate) {
        setStartDate(overrides.startDate);
      }
      if (overrides?.endDate) {
        setEndDate(overrides.endDate);
      }
    },
    [],
  );

  const restoreTabSnapshot = useCallback(
    (tab: 'toZhiche' | 'toErp') => {
      const snapshot = tabSnapshotsRef.current[getSnapshotKey(tab)];
      if (!snapshot) {
        resetTabState();
        return;
      }
      setOrders(snapshot.orders);
      setSyncedOrderIds(new Set(snapshot.syncedOrderIds));
      setExpandedOrderIds(new Set(snapshot.expandedOrderIds));
      setInboundSyncStatusMap({ ...snapshot.inboundSyncStatusMap });
      setOutboundSyncStatusMap({ ...snapshot.outboundSyncStatusMap });
      setHasFetched(snapshot.hasFetched);
      setLastQueryKey(snapshot.lastQueryKey);
      setStartDate(snapshot.startDate);
      setEndDate(snapshot.endDate);
    },
    [getSnapshotKey, resetTabState],
  );

  useEffect(() => {
    const cachedScreenState = erpOrderSyncScreenCache[screenCacheKey];
    if (!cachedScreenState) {
      return;
    }

    tabSnapshotsRef.current = cachedScreenState.tabSnapshots || {};
    setActiveTab(cachedScreenState.activeTab);
    restoreTabSnapshot(cachedScreenState.activeTab);
  }, [restoreTabSnapshot, screenCacheKey]);

  useEffect(() => {
    const loadEmployeeMappings = async () => {
      try {
        const res =
          await storeErpIntegrationService.getBrowserExtensionErpIntegrationEmployeeMappings();
        setEmployeeMappings(res?.items || []);
      } catch (e) {
        console.warn('获取系统员工绑定关系失败', e);
        setEmployeeMappings([]);
      }
    };

    void loadEmployeeMappings();
  }, [storeErpIntegrationService]);

  useEffect(() => {
    const loadErpEmployees = async () => {
      try {
        const res =
          await storeErpIntegrationService.getErpEmployees(integrationId);
        setErpEmployees(res || []);
      } catch (e) {
        console.warn('获取系统员工列表失败', e);
        setErpEmployees([]);
      }
    };

    if (integrationId) {
      void loadErpEmployees();
    }
  }, [integrationId, storeErpIntegrationService]);

  const handleDateConfirm = useCallback(
    (date: Date) => {
      hideDateTimePicker();
      if (datePickerType === 'start') {
        setStartDate(date);
      } else {
        setEndDate(date);
      }
    },
    [datePickerType, hideDateTimePicker],
  );

  const applyQuickRange = useCallback((days: number) => {
    setStartDate(
      dayjs()
        .subtract(days - 1, 'day')
        .toDate(),
    );
    setEndDate(new Date());
  }, []);

  const getOrderTime = useCallback((order: any): number => {
    const raw =
      order.createdAt ||
      order.source?.sourceOrderTimestamp ||
      order.estimatedFinishTime;
    if (!raw) return NaN;
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? NaN : t;
  }, []);

  const getOrderSortFallbackKey = useCallback(
    (order: any): string =>
      String(
        order.orderNo ||
          order.sourceOrderno ||
          order.source?.sourceOrderNo ||
          order.id ||
          '',
      ),
    [],
  );

  const getNumericOrderNo = useCallback(
    (order: any): number => {
      const key = getOrderSortFallbackKey(order);
      if (!key || !/^\d+$/.test(key)) return NaN;
      const n = Number(key);
      return Number.isSafeInteger(n) ? n : NaN;
    },
    [getOrderSortFallbackKey],
  );

  // casstime 的"开始时间"字段是预约/接待时间，可能与实际建单顺序不一致(见调试日志中
  // 2607030013/2607030014 两单的时间错位)，而订单编号(source.sourceOrderNo)是casstime创建
  // 订单时分配的自增单据号，天然随时间单调递增，能准确反映真实的新旧顺序，因此仅对 casstime
  // 优先按编号数字倒序排列；编号不可比较时退化为按可解析的真实时间倒序排列；其他 ERP 仍保持
  // 原有的时间优先排序逻辑不变
  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      if (providerId === 'casstime') {
        const numA = getNumericOrderNo(a);
        const numB = getNumericOrderNo(b);
        if (!Number.isNaN(numA) && !Number.isNaN(numB) && numA !== numB) {
          return numB - numA;
        }
      }
      const timeA = getOrderTime(a);
      const timeB = getOrderTime(b);
      const validA = !Number.isNaN(timeA);
      const validB = !Number.isNaN(timeB);
      if (validA && validB && timeA !== timeB) {
        return timeB - timeA;
      }
      if (validA && !validB) return -1;
      if (!validA && validB) return 1;
      return getOrderSortFallbackKey(b).localeCompare(
        getOrderSortFallbackKey(a),
      );
    });
  }, [
    getNumericOrderNo,
    getOrderSortFallbackKey,
    getOrderTime,
    orders,
    providerId,
  ]);

  const getOrderPlate = (order: any) =>
    String(
      order?.licensePlateNo ||
        order?.licensePlate ||
        order?.carNo ||
        order?.carCode ||
        order?.CarCode ||
        order?.vehicleNo ||
        order?.source?.licensePlateNo ||
        '',
    );

  const fetchErpOrders = useCallback(async () => {
    try {
      setFetching(true);
      const startTime = dayjs(startDate).startOf('day').toISOString();
      const endTime = dayjs(endDate).endOf('day').toISOString();

      const res = await storeErpIntegrationService.pullOrdersFromErp(
        integrationId,
        startTime,
        endTime,
      );
      const fetchedOrders = res || [];
      const inboundSyncedIds = new Set<string>();
      const inboundStatusMap: Record<
        string,
        { result: string; error?: string }
      > = {};
      const fetchedSourceOrderNos = new Set(
        fetchedOrders
          .map(order =>
            String(
              order?.source?.sourceOrderNo ||
                order?.sourceOrderno ||
                order?.orderNo ||
                '',
            ),
          )
          .filter(Boolean),
      );

      // 查询这段时间内的同步日志 (Inbound) - 用于标记已同步 / 同步失败
      try {
        const inboundLogResponses = await Promise.all([
          storeErpIntegrationService.searchErpOrderSyncLogs(
            {
              syncDirection: ErpSyncDirection.Inbound,
              erpProviderId: providerId,
              sourceOrderTimestamp: {
                start: startTime,
                end: endTime,
                startInclusive: true,
                endInclusive: true,
              } as any,
            } as any,
            'desc',
            undefined,
            1000,
          ),
          // Casstime v2 早期同步日志里可能写入了错误的 sourceOrderTimestamp，
          // 这里用 requestedAt 再兜一次，保证重进页面时也能回显已同步状态。
          storeErpIntegrationService.searchErpOrderSyncLogs(
            {
              syncDirection: ErpSyncDirection.Inbound,
              erpProviderId: providerId,
              requestedAt: {
                start: dayjs(startDate)
                  .subtract(30, 'day')
                  .startOf('day')
                  .toISOString(),
                end: new Date().toISOString(),
                startInclusive: true,
                endInclusive: true,
              } as any,
            } as any,
            'desc',
            undefined,
            1000,
          ),
        ]);

        const inboundLogs = inboundLogResponses.flatMap(
          response => response?.data || [],
        );

        if (inboundLogs.length > 0) {
          inboundLogs.forEach((log: any) => {
            const sourceOrderNo = log.sourceOrderno
              ? String(log.sourceOrderno)
              : '';
            if (!sourceOrderNo || inboundStatusMap[sourceOrderNo]) {
              return;
            }
            inboundStatusMap[sourceOrderNo] = {
              result: log.syncResult,
              error: log.syncErrorMsg || undefined,
            };
            if (log.syncResult === ErpSyncResult.Success) {
              inboundSyncedIds.add(sourceOrderNo);
            }
          });
        }
      } catch (err) {
        console.warn('获取同步日志失败', err);
      }

      // 同步日志偶发缺失、时间写错或被分页截断时，
      // 直接以知车里已存在的 Extension 订单作为兜底事实来源。
      try {
        const existingInboundOrders = await orderService.searchOrders(
          {
            createdAt: {
              start: dayjs(startDate)
                .subtract(30, 'day')
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss'),
              end: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              startInclusive: true,
              endInclusive: true,
            } as any,
            orderSource: OrderSourceType.Hosted,
          } as any,
          undefined,
          1000,
        );

        (existingInboundOrders?.data || []).forEach(order => {
          const sourceOrderNo = String(order?.sourceOrderno || '');
          if (!sourceOrderNo || !fetchedSourceOrderNos.has(sourceOrderNo)) {
            return;
          }
          inboundSyncedIds.add(sourceOrderNo);
          if (!inboundStatusMap[sourceOrderNo]) {
            inboundStatusMap[sourceOrderNo] = {
              result: ErpSyncResult.Success,
            };
          }
        });
      } catch (err) {
        console.warn('获取知车已同步订单失败', err);
      }
      setSyncedOrderIds(inboundSyncedIds);
      setInboundSyncStatusMap(inboundStatusMap);

      // 查询这段时间内的同步日志 (Outbound) - 用于标记从知车推过去的单子
      try {
        const outboundLogsRes =
          await storeErpIntegrationService.searchErpOrderSyncLogs(
            {
              syncDirection: ErpSyncDirection.Outbound,
              syncResult: ErpSyncResult.Success,
              erpProviderId: providerId,
              requestedAt: {
                start: dayjs(startDate)
                  .subtract(30, 'day')
                  .startOf('day')
                  .toISOString(), // 扩大时间范围以防推过去的时间较早
                end: endTime,
                startInclusive: true,
                endInclusive: true,
              } as any,
            } as any,
            'desc',
            undefined,
            1000,
          );

        if (outboundLogsRes?.data) {
          const zhicheOriginatedOrderIds = new Set<string>();
          const legacyZhicheOriginatedPlates = new Set<string>();
          outboundLogsRes.data.forEach((log: any) => {
            if (log.resultOrderno) {
              zhicheOriginatedOrderIds.add(String(log.resultOrderno));
            }
            if (
              log.licensePlateNo &&
              (!log.resultOrderno ||
                String(log.resultOrderno) === String(log.sourceOrderno || ''))
            ) {
              legacyZhicheOriginatedPlates.add(String(log.licensePlateNo));
            }
          });

          fetchedOrders.forEach(o => {
            const fetchedOrderId = String(
              o.sourceOrderno ||
                o.orderNo ||
                o.id ||
                o.source?.sourceOrderNo ||
                '',
            );
            const fetchedPlate = getOrderPlate(o);
            if (
              (fetchedOrderId &&
                zhicheOriginatedOrderIds.has(fetchedOrderId)) ||
              (fetchedPlate && legacyZhicheOriginatedPlates.has(fetchedPlate))
            ) {
              o._isZhicheOriginated = true;
            }
          });
        }
      } catch (err) {
        console.warn('获取外发日志以标记数据失败', err);
      }

      // 外发同步日志偶发缺失（如通过接口直接推送、日志写入失败等）时，
      // 以知车订单表为兜底事实来源：知车原生订单(非ERP来源)若已回写ERP单号，
      // 说明它是从知车推送到ERP生成的，对应的ERP订单应标记为"知车订单"。
      try {
        const zhicheOrdersRes = await orderService.searchOrders(
          {
            createdAt: {
              start: dayjs(startDate)
                .subtract(30, 'day')
                .startOf('day')
                .format('YYYY-MM-DD HH:mm:ss'),
              end: dayjs().format('YYYY-MM-DD HH:mm:ss'),
              startInclusive: true,
              endInclusive: true,
            } as any,
          } as any,
          undefined,
          1000,
        );

        const zhicheOriginatedErpNos = new Set<string>();
        (zhicheOrdersRes?.data || []).forEach((order: any) => {
          const localOrderNo = String(order?.orderNo || order?.id || '');
          const resultOrderNo = String(order?.sourceOrderno || '');
          const sourceType =
            order?.source?.type?.toLowerCase() ||
            (typeof order?.source === 'string'
              ? order.source.toLowerCase()
              : '');
          const isErpOriginated =
            sourceType === OrderSourceType.Extension ||
            sourceType === OrderSourceType.Hosted ||
            sourceType === OrderSourceType.OpenApi;
          if (
            !isErpOriginated &&
            !!order?.sourceAppid &&
            !!resultOrderNo &&
            resultOrderNo !== localOrderNo
          ) {
            zhicheOriginatedErpNos.add(resultOrderNo);
          }
        });

        if (zhicheOriginatedErpNos.size > 0) {
          fetchedOrders.forEach((o: any) => {
            if (o._isZhicheOriginated) {
              return;
            }
            const fetchedOrderId = String(
              o.sourceOrderno ||
                o.orderNo ||
                o.id ||
                o.source?.sourceOrderNo ||
                '',
            );
            if (fetchedOrderId && zhicheOriginatedErpNos.has(fetchedOrderId)) {
              o._isZhicheOriginated = true;
            }
          });
        }
      } catch (err) {
        console.warn('知车订单兜底反查失败', err);
      }

      setOrders(fetchedOrders);
      setHasFetched(true);
      setLastQueryKey(currentQueryKey);
      saveTabSnapshot('toZhiche', {
        orders: fetchedOrders,
        syncedOrderIds: inboundSyncedIds,
        inboundSyncStatusMap: inboundStatusMap,
        hasFetched: true,
        lastQueryKey: currentQueryKey,
        startDate,
        endDate,
      });
    } catch (e: any) {
      showErpToast(
        `获取${providerName}订单失败: ` + (e.message || '未知错误'),
        'error',
      );
    } finally {
      setFetching(false);
    }
  }, [
    currentQueryKey,
    endDate,
    integrationId,
    orderService,
    providerId,
    providerName,
    saveTabSnapshot,
    startDate,
    storeErpIntegrationService,
  ]);
  const fetchZhicheOrders = useCallback(async () => {
    try {
      setFetching(true);
      const endTime = dayjs(endDate).endOf('day').toISOString();
      const orderSearchStartTime = dayjs(startDate)
        .startOf('day')
        .format('YYYY-MM-DD HH:mm:ss');
      const orderSearchEndTime = dayjs(endDate)
        .endOf('day')
        .format('YYYY-MM-DD HH:mm:ss');

      const res = await orderService.searchOrders(
        {
          createdAt: {
            start: orderSearchStartTime,
            end: orderSearchEndTime,
            startInclusive: true,
            endInclusive: true,
          } as any,
        },
        undefined,
        500,
      ); // 增加获取知车订单的上限，防止因时间范围太大而漏单

      const fetchedOrders = (res.data || []) as any[];

      // 过滤掉原本就是从 ERP 同步进来的单子
      // fetchedOrders = fetchedOrders.filter(order => {
      //   const sourceType = order.source?.type?.toLowerCase();
      //   return sourceType !== 'extension' && sourceType !== 'openapi';
      // });

      // 查询近期入站同步日志，用于标记 ERP 同步进来的单子
      try {
        const inboundLogsRes =
          await storeErpIntegrationService.searchErpOrderSyncLogs(
            {
              syncDirection: ErpSyncDirection.Inbound,
              syncResult: ErpSyncResult.Success,
              erpProviderId: providerId,
              requestedAt: {
                start: dayjs(startDate)
                  .subtract(30, 'day')
                  .startOf('day')
                  .toISOString(), // 扩大时间范围以防推过去的时间较早
                end: endTime,
                startInclusive: true,
                endInclusive: true,
              } as any,
            } as any,
            'desc',
            undefined,
            1000,
          );

        if (inboundLogsRes?.data) {
          const erpOriginatedIds = new Set<string>();
          inboundLogsRes.data.forEach((log: any) => {
            if (log.resultOrderno) {
              erpOriginatedIds.add(String(log.resultOrderno));
            }
          });
          fetchedOrders.forEach(o => {
            const orderId = String(
              o.sourceOrderno || o.orderNo || o.id || o.source?.sourceOrderNo,
            );
            if (erpOriginatedIds.has(orderId)) {
              o._isErpOriginated = true;
            }

            // 兜底逻辑：如果包含 source.type === extension 或 openapi，也认为是 ERP 订单
            const sourceType =
              o.source?.type?.toLowerCase() ||
              (typeof o.source === 'string' ? o.source.toLowerCase() : '');
            if (sourceType === 'extension' || sourceType === 'openapi') {
              o._isErpOriginated = true;
            }
          });
        } else {
          // 如果查询不到日志，走兜底判断
          fetchedOrders.forEach(o => {
            const sourceType =
              o.source?.type?.toLowerCase() ||
              (typeof o.source === 'string' ? o.source.toLowerCase() : '');
            if (sourceType === 'extension' || sourceType === 'openapi') {
              o._isErpOriginated = true;
            }
          });
        }
      } catch (err) {
        console.warn('获取入站同步日志以标记数据失败', err);
        // 如果报错了，走兜底判断
        fetchedOrders.forEach(o => {
          const sourceType =
            o.source?.type?.toLowerCase() ||
            (typeof o.source === 'string' ? o.source.toLowerCase() : '');
          if (sourceType === 'extension' || sourceType === 'openapi') {
            o._isErpOriginated = true;
          }
        });
      }

      setOrders(fetchedOrders);

      // 查询这段时间内的同步日志 (外发) - 用于标记已同步 / 同步失败
      const syncedIds = new Set<string>();
      const statusMap: Record<string, { result: string; error?: string }> = {};
      try {
        const logsRes = await storeErpIntegrationService.searchErpOrderSyncLogs(
          {
            syncDirection: ErpSyncDirection.Outbound,
            erpProviderId: providerId,
          } as any,
          'desc',
          undefined,
          1000,
        );

        if (logsRes?.data) {
          logsRes.data.forEach((log: any) => {
            const sourceOrderNo = log.sourceOrderno
              ? String(log.sourceOrderno)
              : '';
            if (!sourceOrderNo || statusMap[sourceOrderNo]) {
              return;
            }
            statusMap[sourceOrderNo] = {
              result: log.syncResult,
              error: log.syncErrorMsg || undefined,
            };
            if (log.syncResult === ErpSyncResult.Success) {
              syncedIds.add(sourceOrderNo);
            }
          });
        }
      } catch (err) {
        console.warn('获取外发同步日志失败', err);
      }

      // 兜底：如果订单表本身已经回写了 ERP 单号和来源应用，说明实际已同步成功。
      fetchedOrders.forEach(order => {
        const localOrderNo = String(getOutboundSourceOrderNo(order) || '');
        const resultOrderNo = String(order?.sourceOrderno || '');
        const hasSyncedFact =
          !!order?.sourceAppid &&
          !!resultOrderNo &&
          !!localOrderNo &&
          resultOrderNo !== localOrderNo &&
          !order?._isErpOriginated;

        if (!hasSyncedFact) {
          return;
        }

        syncedIds.add(localOrderNo);
        if (!statusMap[localOrderNo]) {
          statusMap[localOrderNo] = {
            result: ErpSyncResult.Success,
          };
        }
      });

      setSyncedOrderIds(syncedIds);
      setOutboundSyncStatusMap(statusMap);
      setHasFetched(true);
      setLastQueryKey(currentQueryKey);
      saveTabSnapshot('toErp', {
        orders: fetchedOrders,
        syncedOrderIds: syncedIds,
        outboundSyncStatusMap: statusMap,
        hasFetched: true,
        lastQueryKey: currentQueryKey,
        startDate,
        endDate,
      });
    } catch (e: any) {
      showErpToast('获取知车订单失败: ' + (e.message || '未知错误'), 'error');
    } finally {
      setFetching(false);
    }
  }, [
    currentQueryKey,
    endDate,
    orderService,
    providerId,
    saveTabSnapshot,
    startDate,
    storeErpIntegrationService,
  ]);

  const onTabChange = (tab: 'toZhiche' | 'toErp') => {
    saveTabSnapshot(activeTab);
    setActiveTab(tab);
    restoreTabSnapshot(tab);
    erpOrderSyncScreenCache[screenCacheKey] = {
      activeTab: tab,
      tabSnapshots: { ...tabSnapshotsRef.current },
    };
  };

  const onFetchPress = () => {
    if (activeTab === 'toZhiche') {
      void fetchErpOrders();
    } else {
      void fetchZhicheOrders();
    }
  };

  const getInboundOrderId = (order: any) =>
    order.sourceOrderno ||
    order.orderNo ||
    order.id ||
    order.source?.sourceOrderNo;

  const getOutboundSourceOrderNo = (order: any) =>
    order.orderNo ||
    order.id ||
    order.source?.sourceOrderNo ||
    order.sourceOrderno;

  const getStatusTone = useCallback(
    (tone: 'source' | 'success' | 'danger' | 'action') => {
      switch (tone) {
        case 'source':
          return {
            textColor: '#1677ff',
            backgroundColor: '#e6f4ff',
            borderColor: '#91caff',
          };
        case 'success':
          return {
            textColor: '#52c41a',
            backgroundColor: '#f6ffed',
            borderColor: '#b7eb8f',
          };
        case 'danger':
          return {
            textColor: '#ff4d4f',
            backgroundColor: '#fff2f0',
            borderColor: '#ffccc7',
          };
        default:
          return {
            textColor: '#5ba0e0',
            backgroundColor: '#f0f7ff',
            borderColor: '#5ba0e0',
          };
      }
    },
    [],
  );

  const getOrderEmployeeDiagnostics = useCallback(
    (order: any) => {
      const source = order.source || {};
      const erpEmployeeId = source.erpServiceAgentEmployeeId;
      const erpServiceAgentName =
        source.erpServiceAgentName ||
        source.erpServiceAgentRawValue ||
        source.serviceAgentName;
      const erpServiceAgentMobile = source.erpServiceAgentMobile;
      const erpServiceAgentRawValue = source.erpServiceAgentRawValue;
      const mapping = employeeMappings.find(
        item =>
          String(item.erpEmployeeId || '') === String(erpEmployeeId || ''),
      );

      let blockReason = '';
      if (!erpServiceAgentName) {
        blockReason = `缺少${providerName}接车顾问姓名`;
      } else if (!erpEmployeeId) {
        blockReason = `缺少${providerName}接车顾问员工ID`;
      } else if (!mapping) {
        blockReason = `该${providerName}接车顾问还未绑定知车员工`;
      } else if (!source.serviceAgentId) {
        blockReason = '已绑定，但订单中缺少知车员工ID';
      } else if (!source.serviceAgentName) {
        blockReason = '已绑定，但订单中缺少知车员工姓名';
      } else if (!source.serviceAgentMobile) {
        blockReason = '已绑定，但订单中缺少知车员工手机号';
      }

      return {
        erpEmployeeId,
        erpServiceAgentName,
        erpServiceAgentMobile,
        erpServiceAgentRawValue,
        mapping,
        blockReason,
      };
    },
    [employeeMappings, providerName],
  );

  const findErpEmployeeById = useCallback(
    (erpEmployeeId: string | number | undefined) => {
      if (erpEmployeeId == null) return undefined;
      return erpEmployees.find(item => {
        const candidateIds = [
          item?.id,
          item?.idEmployee,
          item?.ID,
          item?.PersonID,
          item?.employeeId,
        ];
        return candidateIds.some(
          id => String(id || '') === String(erpEmployeeId),
        );
      });
    },
    [erpEmployees],
  );

  const getOutboundEmployeeDiagnostics = useCallback(
    (order: any) => {
      const zhicheServiceAgentId =
        order.serviceAgentId || order.source?.serviceAgentId;
      const zhicheServiceAgentName =
        order.serviceAgentName || order.source?.serviceAgentName;
      const mapping = employeeMappings.find(
        item =>
          String(item.memberId || '') === String(zhicheServiceAgentId || ''),
      );
      const erpEmployee = findErpEmployeeById(mapping?.erpEmployeeId);
      const erpEmployeeName =
        erpEmployee?.name ||
        erpEmployee?.employeeName ||
        erpEmployee?.Name ||
        erpEmployee?.PersonName;
      const erpEmployeeMobile = erpEmployee?.mobile || erpEmployee?.Mobile;

      let blockReason = '';
      if (!zhicheServiceAgentId) {
        blockReason = '缺少知车服务顾问ID';
      } else if (!zhicheServiceAgentName) {
        blockReason = '缺少知车服务顾问姓名';
      } else if (!mapping) {
        blockReason = `该知车服务顾问还未绑定${providerName}员工`;
      } else if (!erpEmployee) {
        blockReason = `绑定的${providerName}员工在当前${providerName}员工列表中不存在`;
      }

      return {
        zhicheServiceAgentId,
        zhicheServiceAgentName,
        mapping,
        erpEmployee,
        erpEmployeeId: mapping?.erpEmployeeId,
        erpEmployeeName,
        erpEmployeeMobile,
        blockReason,
      };
    },
    [employeeMappings, findErpEmployeeById, providerName],
  );

  const syncToZhiche = async (order: any) => {
    const orderId = getInboundOrderId(order);
    if (syncedOrderIds.has(orderId)) return;

    try {
      setSyncingOrderId(String(orderId || ''));
      const employeeDiagnostics = getOrderEmployeeDiagnostics(order);
      const source = {
        ...(order.source || {}),
        serviceAgentId:
          order.source?.serviceAgentId || employeeDiagnostics.mapping?.memberId,
        serviceAgentName:
          order.source?.serviceAgentId && order.source?.serviceAgentName
            ? order.source.serviceAgentName
            : employeeDiagnostics.mapping?.memberName ||
              order.source?.serviceAgentName,
        serviceAgentMobile:
          order.source?.serviceAgentId && order.source?.serviceAgentMobile
            ? order.source.serviceAgentMobile
            : employeeDiagnostics.mapping?.memberMobile ||
              order.source?.serviceAgentMobile,
      };

      if (!source.serviceAgentName) {
        throw new Error(`${providerName}订单缺少接车顾问姓名`);
      }
      if (!source.serviceAgentMobile) {
        throw new Error(`${providerName}订单缺少接车顾问手机号`);
      }
      if (!source.erpServiceAgentEmployeeId) {
        throw new Error(`${providerName}订单缺少接车顾问员工ID`);
      }

      // Construct a valid request
      const request: any = {
        ...order,
        orderType: order.orderType ? order.orderType.toLowerCase() : 'basic',
        source: {
          ...source,
          // This screen is only reachable from a hosted integration, and the
          // order has to record the one it actually came through.
          type: OrderSourceType.Hosted,
          integrationId,
          erpProviderId: providerId,
          sourceOrderNo: orderId || '',
          origin: ErpSyncOrigin.User,
          sourceOrderTimestamp:
            source.sourceOrderTimestamp ||
            order.createdAt ||
            new Date().toISOString(),
          clientSlug: 'euler',
          clientVersion: '1.0.0',
        },
      };
      await storeErpIntegrationService.syncOrderFromErp(request);
      const nextSyncedIds = new Set(syncedOrderIds).add(orderId);
      const nextInboundStatusMap = {
        ...inboundSyncStatusMap,
        [String(orderId || '')]: {
          result: ErpSyncResult.Success,
        },
      };
      setSyncedOrderIds(nextSyncedIds);
      setInboundSyncStatusMap(nextInboundStatusMap);
      saveTabSnapshot('toZhiche', {
        syncedOrderIds: nextSyncedIds,
        inboundSyncStatusMap: nextInboundStatusMap,
      });
      showErpToast('同步成功', 'success');
    } catch (e: any) {
      const nextInboundStatusMap = {
        ...inboundSyncStatusMap,
        [String(orderId || '')]: {
          result: ErpSyncResult.Failed,
          error: e.message || '未知错误',
        },
      };
      setInboundSyncStatusMap(nextInboundStatusMap);
      saveTabSnapshot('toZhiche', {
        inboundSyncStatusMap: nextInboundStatusMap,
      });
      showErpToast('同步失败: ' + (e.message || '未知错误'), 'error');
    } finally {
      setSyncingOrderId(null);
    }
  };

  const syncToErp = async (order: any) => {
    const orderId = getOutboundSourceOrderNo(order);
    if (syncedOrderIds.has(orderId)) return;

    const startSyncTime = new Date().toISOString();

    try {
      setSyncingOrderId(String(orderId || ''));
      const employeeDiagnostics = getOutboundEmployeeDiagnostics(order);
      if (employeeDiagnostics.blockReason) {
        throw new Error(employeeDiagnostics.blockReason);
      }
      const pushResult = await storeErpIntegrationService.pushOrderToErp(
        integrationId,
        order,
      );

      // 记录外发同步日志
      try {
        const integrationRes =
          await storeErpIntegrationService.getBrowserExtensionErpIntegration();
        if (integrationRes) {
          await storeErpIntegrationService.logErpOutboundOrderSyncResult({
            integrationId: integrationRes.id,
            syncResult: {
              type: ErpSyncResult.Success,
              resultOrderno: pushResult?.resultOrderno,
            },
            sourceOrderno: orderId,
            origin: ErpSyncOrigin.User,
            startSyncTime,
            finishedSyncTime: new Date().toISOString(),
            requestedBy: 0,
            requestedByName: 'EulerUser',
            clientSlug: 'euler',
            clientVersion: '1.0.0',
          } as any);
        }
      } catch (logErr) {
        console.warn('记录外发日志失败', logErr);
      }

      const nextSyncedIds = new Set(syncedOrderIds).add(orderId);
      const nextOutboundStatusMap = {
        ...outboundSyncStatusMap,
        [String(orderId || '')]: {
          result: ErpSyncResult.Success,
        },
      };
      setSyncedOrderIds(nextSyncedIds);
      setOutboundSyncStatusMap(nextOutboundStatusMap);
      saveTabSnapshot('toErp', {
        syncedOrderIds: nextSyncedIds,
        outboundSyncStatusMap: nextOutboundStatusMap,
      });
      showErpToast('同步成功', 'success');
    } catch (e: any) {
      // 记录失败日志
      try {
        const integrationRes =
          await storeErpIntegrationService.getBrowserExtensionErpIntegration();
        if (integrationRes) {
          await storeErpIntegrationService.logErpOutboundOrderSyncResult({
            integrationId: integrationRes.id,
            syncResult: {
              type: ErpSyncResult.Failed,
              error: e.message || '未知错误',
            },
            sourceOrderno: orderId,
            origin: ErpSyncOrigin.User,
            startSyncTime,
            finishedSyncTime: new Date().toISOString(),
            requestedBy: 0,
            requestedByName: 'EulerUser',
            clientSlug: 'euler',
            clientVersion: '1.0.0',
          } as any);
        }
      } catch (logErr) {
        console.warn('记录外发日志失败', logErr);
      }

      const nextOutboundStatusMap = {
        ...outboundSyncStatusMap,
        [String(orderId || '')]: {
          result: ErpSyncResult.Failed,
          error: e.message || '未知错误',
        },
      };
      setOutboundSyncStatusMap(nextOutboundStatusMap);
      saveTabSnapshot('toErp', {
        outboundSyncStatusMap: nextOutboundStatusMap,
      });
      showErpToast('同步失败: ' + (e.message || '未知错误'), 'error');
    } finally {
      setSyncingOrderId(null);
    }
  };

  const renderInfoRow = (
    label: string,
    value: string | number | undefined,
    options?: {
      valueColor?: string;
      emphasize?: boolean;
      marginBottom?: number;
    },
  ) => {
    const displayValue =
      value == null || value === ''
        ? '-'
        : typeof value === 'string'
        ? value
        : String(value);
    return (
      <View
        key={`${label}-${displayValue}`}
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          marginBottom: options?.marginBottom ?? 8,
        }}
      >
        <Text
          style={{
            width: 88,
            fontSize: 11.5,
            lineHeight: 17,
            color: '#98a2b3',
          }}
        >
          {label}
        </Text>
        <Text
          style={{
            flex: 1,
            fontSize: options?.emphasize ? 12.5 : 12,
            lineHeight: 17,
            color: options?.valueColor || '#344054',
            fontWeight: options?.emphasize ? '600' : '400',
          }}
        >
          {displayValue}
        </Text>
      </View>
    );
  };

  const extraInfoFieldGroups = useMemo(
    () => ({
      toZhiche: {
        order: [
          'vin',
          'vehicleBrandName',
          'vehicleYear',
          'vehicleDisplacement',
          'vehicleFuelTypeNormalized',
          'vehicleFuelType',
          'vehicleMileage',
          'orderType',
          'createdAt',
          'updatedAt',
          'insuranceExpiryDate',
          'arriveTime',
          'deliveryTime',
        ],
        source: ['sourceOrderNo', 'sourceOrderTimestamp', 'origin', 'type'],
      },
      toErp: {
        order: [
          'vin',
          'vehicleBrandName',
          'vehicleYear',
          'vehicleDisplacement',
          'vehicleFuelTypeNormalized',
          'vehicleFuelType',
          'vehicleMileage',
          'orderType',
          'createdAt',
          'updatedAt',
          'returnType',
          'reservationType',
          'carwashType',
          'oldPartDisposalType',
        ],
        source: ['sourceOrderNo', 'sourceOrderTimestamp', 'origin', 'type'],
      },
    }),
    [],
  );

  const extraInfoLabelMap = useMemo(
    () => ({
      orgId: '组织ID',
      storeId: '门店ID',
      orderType: '订单类型',
      reportNo: '报告编号',
      isMainOrder: '主订单',
      isFirstTimeOrder: '首次到店',
      vin: '车架号',
      vehicleBrandName: '车辆品牌',
      vehicleModel: '车型',
      vehicleModel2: '车型2',
      vehicleYear: '年款',
      vehicleDisplacement: '排量',
      vehicleFuelType: '燃油类型',
      vehicleFuelTypeNormalized: '标准燃油类型',
      vehicleMileage: '里程',
      serviceAgentId: '服务顾问ID',
      serviceAgentName: '服务顾问',
      status: '订单状态',
      createdAt: '创建时间',
      updatedAt: '更新时间',
      customerId: '客户ID',
      customerType: '客户类型',
      insuranceExpiryDate: '保险到期',
      returnType: '返车方式',
      reservationType: '预约类型',
      oldPartDisposalType: '旧件处理',
      carwashType: '洗车方式',
      sourceAppid: '来源应用ID',
      sourceRequestId: '来源请求ID',
      deliveryTime: '交车时间',
      arriveTime: '到店时间',
      pickUpTime: '接车时间',
      sourceOrderTimestamp: '来源订单时间',
      requestMemberId: '请求人ID',
      requestMemberName: '请求人',
      origin: '触发来源',
      clientSlug: '客户端',
      clientVersion: '客户端版本',
      erpProviderId: '集成服务商',
      sourceOrderNo: '来源订单号',
      type: '来源类型',
      plateNumber: '车牌号',
      licensePlateNo: '车牌号',
      mobile: '手机号',
      phone: '电话',
      memberId: '会员ID',
      memberName: '会员姓名',
      remark: '备注',
      source: '来源信息',
    }),
    [],
  );

  const extraInfoValueMap = useMemo(
    () => ({
      basic: '基础工单',
      Basic: '基础工单',
      default: '默认',
      extension: `${providerName}插件`,
      openapi: '开放接口',
      system: `${providerName}触发`,
      user: '人工触发',
      inbound: `${providerName}到知车`,
      outbound: `知车到${providerName}`,
      success: '成功',
      failed: '失败',
      pending: '待处理',
      inprogress: '进行中',
      finished: '已完成',
      canceled: '已取消',
      gasoline: '汽油',
      diesel: '柴油',
      ng: '天然气',
      ev: '纯电',
      phev: '插电混动',
      hev: '油电混动',
      fcev: '氢燃料',
      methanol: '甲醇',
      true: '是',
      false: '否',
      none: '无',
      None: '无',
      NoWash: '不洗车',
      PaidWash: '付费洗车',
      FreeWash: '免费洗车',
      Reserved: '已预约',
      NonReserved: '非预约',
      OnSite: '到店',
      Deliver: '送车',
      Pick: '取车',
    }),
    [providerName],
  );

  const translateExtraLabel = useCallback(
    (key: string, prefix?: string) => {
      const translatedKey =
        extraInfoLabelMap[key as keyof typeof extraInfoLabelMap] || key;
      if (!prefix) {
        return translatedKey;
      }
      return `${prefix}${translatedKey}`;
    },
    [extraInfoLabelMap],
  );

  const formatExtraValue = useCallback(
    (key: string, value: any): string => {
      if (value == null || value === '') {
        return '';
      }
      const lowerKey = key.toLowerCase();
      const isDateField =
        lowerKey.endsWith('date') ||
        lowerKey.endsWith('time') ||
        lowerKey.endsWith('at');

      const getNumberEnumText = (fieldKey: string, fieldValue: number) => {
        if (fieldKey === 'returnType') {
          return (
            ['无', '到店', '送车', '取车'][fieldValue] || String(fieldValue)
          );
        }
        if (fieldKey === 'reservationType') {
          return ['无', '已预约', '非预约'][fieldValue] || String(fieldValue);
        }
        if (fieldKey === 'carwashType') {
          return (
            ['无', '不洗车', '付费洗车', '免费洗车'][fieldValue] ||
            String(fieldValue)
          );
        }
        if (
          fieldKey === 'isMainOrder' ||
          fieldKey === 'isFirstTimeOrder' ||
          fieldKey === 'isConstructionJobScheduled'
        ) {
          return fieldValue ? '是' : '否';
        }
        return undefined;
      };

      if (typeof value === 'string') {
        if (isDateField && dayjs(value).isValid()) {
          return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        }
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
        }
        return (
          extraInfoValueMap[value as keyof typeof extraInfoValueMap] || value
        );
      }
      if (typeof value === 'boolean') {
        return value ? '是' : '否';
      }
      if (typeof value === 'number') {
        const mappedByKey = getNumberEnumText(key, value);
        if (mappedByKey) {
          return mappedByKey;
        }
        return String(value);
      }
      if (Array.isArray(value)) {
        const compactItems = value
          .map(item => formatExtraValue(key, item))
          .filter(Boolean);
        return compactItems.join(' / ');
      }
      if (value instanceof Date) {
        return dayjs(value).format('YYYY-MM-DD HH:mm:ss');
      }
      if (typeof value === 'object') {
        try {
          return JSON.stringify(value);
        } catch (e) {
          return String(value);
        }
      }
      return String(value);
    },
    [extraInfoValueMap],
  );

  const appendExtraInfoEntries = useCallback(
    (
      entries: Array<{ label: string; value: string }>,
      target: Record<string, any> | undefined,
      keys: string[],
      prefixLabel?: string,
    ) => {
      if (!target) return;
      keys.forEach(key => {
        const value = formatExtraValue(key, target[key]);
        if (!value) {
          return;
        }
        entries.push({
          label: translateExtraLabel(key, prefixLabel),
          value,
        });
      });
    },
    [formatExtraValue, translateExtraLabel],
  );

  const getExtraInfoEntries = useCallback(
    (order: any) => {
      const entries: Array<{ label: string; value: string }> = [];
      const fieldGroup = extraInfoFieldGroups[activeTab];

      appendExtraInfoEntries(entries, order, fieldGroup.order);

      if (activeTab === 'toErp' && order?.remark) {
        appendExtraInfoEntries(entries, order, ['remark']);
      }
      if (order?.source && typeof order.source === 'object') {
        appendExtraInfoEntries(
          entries,
          order.source,
          fieldGroup.source,
          '来源',
        );
      }

      return entries;
    },
    [activeTab, appendExtraInfoEntries, extraInfoFieldGroups],
  );

  const querySummary = useMemo(() => {
    return sortedOrders.reduce(
      (acc, order) => {
        const orderId =
          activeTab === 'toZhiche'
            ? getInboundOrderId(order)
            : getOutboundSourceOrderNo(order);
        const latestSyncStatus =
          activeTab === 'toZhiche'
            ? inboundSyncStatusMap[String(orderId || '')]
            : outboundSyncStatusMap[String(orderId || '')];
        const employeeDiagnostics =
          activeTab === 'toZhiche'
            ? getOrderEmployeeDiagnostics(order)
            : undefined;
        const outboundEmployeeDiagnostics =
          activeTab === 'toErp'
            ? getOutboundEmployeeDiagnostics(order)
            : undefined;

        if (
          (activeTab === 'toZhiche' && order._isZhicheOriginated) ||
          (activeTab === 'toErp' && order._isErpOriginated)
        ) {
          acc.source += 1;
        } else if (
          syncedOrderIds.has(orderId) ||
          latestSyncStatus?.result === ErpSyncResult.Success
        ) {
          acc.synced += 1;
        } else if (latestSyncStatus?.result === ErpSyncResult.Failed) {
          acc.failed += 1;
        } else if (
          employeeDiagnostics?.blockReason ||
          outboundEmployeeDiagnostics?.blockReason
        ) {
          acc.blocked += 1;
        } else {
          acc.pending += 1;
        }
        return acc;
      },
      { pending: 0, synced: 0, blocked: 0, failed: 0, source: 0 },
    );
  }, [
    activeTab,
    getOrderEmployeeDiagnostics,
    getOutboundEmployeeDiagnostics,
    inboundSyncStatusMap,
    outboundSyncStatusMap,
    sortedOrders,
    syncedOrderIds,
  ]);

  return (
    <View style={{ flex: 1, backgroundColor: '#f7f7f7' }}>
      <View
        style={{
          backgroundColor: '#fff',
          borderBottomWidth: 1,
          borderBottomColor: '#eee',
          paddingHorizontal: 12,
          paddingVertical: 12,
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            backgroundColor: '#f2f4f7',
            borderRadius: 16,
            padding: 4,
          }}
        >
          <TouchableOpacity
            style={{
              flex: 1,
              paddingVertical: 12,
              paddingHorizontal: 12,
              alignItems: 'center',
              borderRadius: 12,
              backgroundColor:
                activeTab === 'toZhiche' ? '#ffffff' : 'transparent',
              shadowColor: activeTab === 'toZhiche' ? '#101828' : 'transparent',
              shadowOpacity: activeTab === 'toZhiche' ? 0.08 : 0,
              shadowRadius: 8,
              shadowOffset: { width: 0, height: 2 },
              elevation: activeTab === 'toZhiche' ? 1 : 0,
            }}
            onPress={() => onTabChange('toZhiche')}
          >
            <Text
              style={{
                color: activeTab === 'toZhiche' ? '#2669db' : Colors.Gray2,
                fontWeight: '700',
                fontSize: 15,
              }}
            >
              {`${providerName}同步到知车`}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={{
              flex: 1,
              paddingVertical: 12,
              paddingHorizontal: 12,
              alignItems: 'center',
              borderRadius: 12,
              backgroundColor:
                activeTab === 'toErp' ? '#ffffff' : 'transparent',
              shadowColor: activeTab === 'toErp' ? '#101828' : 'transparent',
              shadowOpacity: activeTab === 'toErp' ? 0.08 : 0,
              shadowRadius: 8,
              shadowOffset: { width: 0, height: 2 },
              elevation: activeTab === 'toErp' ? 1 : 0,
            }}
            onPress={() => onTabChange('toErp')}
          >
            <Text
              style={{
                color: activeTab === 'toErp' ? '#2669db' : Colors.Gray2,
                fontWeight: '700',
                fontSize: 15,
              }}
            >
              {`知车同步到${providerName}`}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      <View
        style={{
          padding: 15,
          backgroundColor: '#fff',
          borderBottomWidth: 1,
          borderBottomColor: '#f0f0f0',
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              flex: 1,
              marginRight: 10,
            }}
          >
            <Text style={{ color: Colors.Gray2, marginRight: 8, fontSize: 14 }}>
              查询:
            </Text>
            <TouchableOpacity
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: 6,
                borderWidth: 1,
                borderColor: '#e4e7eb',
                paddingVertical: 8,
                paddingHorizontal: 10,
                flex: 1,
                alignItems: 'center',
              }}
              onPress={() => {
                setDatePickerType('start');
                showDateTimePicker();
              }}
            >
              <Text style={{ color: '#333', fontSize: 14 }}>
                {dayjs(startDate).format('YYYY-MM-DD')}
              </Text>
            </TouchableOpacity>
            <Text style={{ color: Colors.Gray2, marginHorizontal: 8 }}>至</Text>
            <TouchableOpacity
              style={{
                backgroundColor: '#f8f9fa',
                borderRadius: 6,
                borderWidth: 1,
                borderColor: '#e4e7eb',
                paddingVertical: 8,
                paddingHorizontal: 10,
                flex: 1,
                alignItems: 'center',
              }}
              onPress={() => {
                setDatePickerType('end');
                showDateTimePicker();
              }}
            >
              <Text style={{ color: '#333', fontSize: 14 }}>
                {dayjs(endDate).format('YYYY-MM-DD')}
              </Text>
            </TouchableOpacity>
          </View>
          <Button
            title="查询订单"
            style={{
              borderRadius: 6,
              backgroundColor: '#5ba0e0',
              paddingHorizontal: 12,
              height: 38,
            }}
            textStyle={{ color: '#fff', fontSize: 14, fontWeight: '500' }}
            onPress={onFetchPress}
            loading={fetching}
          />
        </View>
        <View style={{ flexDirection: 'row', marginTop: 12 }}>
          {[1, 3, 7, 30].map(days => {
            const selected =
              dayjs(startDate).format('YYYY-MM-DD') ===
                dayjs()
                  .subtract(days - 1, 'day')
                  .format('YYYY-MM-DD') &&
              dayjs(endDate).format('YYYY-MM-DD') ===
                dayjs().format('YYYY-MM-DD');
            return (
              <TouchableOpacity
                key={days}
                style={{
                  paddingHorizontal: 12,
                  paddingVertical: 7,
                  borderRadius: 999,
                  backgroundColor: selected ? '#eaf3ff' : '#f8f9fa',
                  marginRight: 8,
                }}
                onPress={() => applyQuickRange(days)}
              >
                <Text
                  style={{
                    fontSize: 12,
                    color: selected ? '#2669db' : '#667085',
                  }}
                >
                  {days === 1 ? '今天' : `${days}天`}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
        {hasPendingFilters && (
          <View
            style={{
              marginTop: 12,
              backgroundColor: '#fff7e8',
              borderRadius: 12,
              paddingHorizontal: 12,
              paddingVertical: 10,
            }}
          >
            <Text style={{ color: '#ad6800', fontSize: 12.5, lineHeight: 18 }}>
              筛选条件已变更，点击“查询订单”后再刷新当前列表。
            </Text>
          </View>
        )}
      </View>

      <ScrollView style={{ flex: 1 }}>
        <View
          style={{ paddingHorizontal: 12, paddingTop: 14, paddingBottom: 24 }}
        >
          {sortedOrders.length > 0 && (
            <View
              style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                marginBottom: 12,
              }}
            >
              <SummaryChip
                label="待同步"
                value={querySummary.pending}
                tone="pending"
              />
              <SummaryChip
                label="已同步"
                value={querySummary.synced}
                tone="success"
              />
              <SummaryChip
                label="不可同步"
                value={querySummary.blocked}
                tone="danger"
              />
              <SummaryChip
                label="失败"
                value={querySummary.failed}
                tone="danger"
              />
            </View>
          )}
          <Text
            style={{
              fontSize: 13,
              color: '#98a2b3',
              fontWeight: '600',
              marginBottom: 10,
              marginLeft: 2,
            }}
          >
            订单列表{sortedOrders.length > 0 ? ` (${sortedOrders.length})` : ''}
          </Text>
          {fetching && sortedOrders.length === 0 ? (
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                paddingVertical: 32,
                paddingHorizontal: 16,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: '#eaecf0',
              }}
            >
              <ActivityIndicator size="small" color="#5ba0e0" />
              <Text
                style={{ fontSize: 14, color: Colors.Gray2, marginTop: 12 }}
              >
                正在加载订单...
              </Text>
            </View>
          ) : !hasFetched && sortedOrders.length === 0 ? (
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                paddingVertical: 32,
                paddingHorizontal: 16,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: '#eaecf0',
              }}
            >
              <Text style={{ fontSize: 14, color: Colors.Gray2 }}>
                请选择时间范围并点击“查询订单”
              </Text>
            </View>
          ) : sortedOrders.length === 0 && !fetching ? (
            <View
              style={{
                backgroundColor: '#fff',
                borderRadius: 14,
                paddingVertical: 32,
                paddingHorizontal: 16,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: '#eaecf0',
              }}
            >
              <Text style={{ fontSize: 14, color: Colors.Gray2 }}>
                {activeTab === 'toZhiche'
                  ? `该时间段暂无${providerName}订单`
                  : '该时间段暂无知车订单'}
              </Text>
            </View>
          ) : (
            sortedOrders.map((order, index) => {
              const orderId =
                activeTab === 'toZhiche'
                  ? getInboundOrderId(order)
                  : getOutboundSourceOrderNo(order);
              const isSynced = syncedOrderIds.has(orderId);
              const employeeDiagnostics =
                activeTab === 'toZhiche'
                  ? getOrderEmployeeDiagnostics(order)
                  : null;
              const outboundEmployeeDiagnostics =
                activeTab === 'toErp'
                  ? getOutboundEmployeeDiagnostics(order)
                  : null;
              const latestSyncStatus =
                activeTab === 'toZhiche'
                  ? inboundSyncStatusMap[String(orderId || '')]
                  : outboundSyncStatusMap[String(orderId || '')];

              let actionText =
                activeTab === 'toZhiche'
                  ? '同步到知车'
                  : `同步到${providerName}`;
              let isActionDisabled = false;
              let badgeText = '';
              let badgeTone: 'source' | 'success' | 'danger' | 'action' =
                'action';
              let stateText = '待同步';
              let stateColor = Colors.Gray2;

              if (activeTab === 'toZhiche' && order._isZhicheOriginated) {
                badgeText = '知车订单';
                badgeTone = 'source';
                isActionDisabled = true;
                stateText = '来源单';
                stateColor = '#1677ff';
              } else if (activeTab === 'toErp' && order._isErpOriginated) {
                badgeText = `${providerName}订单`;
                badgeTone = 'source';
                isActionDisabled = true;
                stateText = '来源单';
                stateColor = '#1677ff';
              } else if (
                isSynced ||
                latestSyncStatus?.result === ErpSyncResult.Success
              ) {
                badgeText = '已同步';
                badgeTone = 'success';
                isActionDisabled = true;
                stateText = '已同步';
                stateColor = StatusColors.Success;
              } else if (
                activeTab === 'toZhiche' &&
                employeeDiagnostics?.blockReason
              ) {
                badgeText = '不可同步';
                badgeTone = 'danger';
                isActionDisabled = true;
                stateText = '不可同步';
                stateColor = StatusColors.Danger;
              } else if (
                activeTab === 'toErp' &&
                outboundEmployeeDiagnostics?.blockReason
              ) {
                badgeText = '不可同步';
                badgeTone = 'danger';
                isActionDisabled = true;
                stateText = '不可同步';
                stateColor = StatusColors.Danger;
              } else if (latestSyncStatus?.result === ErpSyncResult.Failed) {
                actionText = '重新同步';
                stateText = '同步失败';
                stateColor = StatusColors.Danger;
              }

              const toneStyle = getStatusTone(badgeTone);
              const isSyncing = syncingOrderId === String(orderId || '');
              const plateText =
                getOrderPlate(order) ||
                String(order.orderNo || order.id || '未知车牌');
              const vehicleText = order.vehicleName || '-';
              const customerText = [
                order.customerName || '未知',
                order.customerMobile || '',
              ]
                .filter(Boolean)
                .join(' ');
              const orderNoText =
                orderId && String(orderId) !== String(plateText)
                  ? String(orderId)
                  : '';
              const extraInfoEntries = getExtraInfoEntries(order);
              const isExpanded = expandedOrderIds.has(String(orderId || index));

              return (
                <View
                  key={`${orderId || 'order'}-${index}`}
                  style={{
                    backgroundColor: '#fff',
                    borderRadius: 12,
                    padding: 12,
                    marginBottom: 10,
                    borderWidth: 1,
                    borderColor: '#eaecf0',
                    shadowColor: '#101828',
                    shadowOpacity: 0.04,
                    shadowRadius: 8,
                    shadowOffset: { width: 0, height: 2 },
                    elevation: 1,
                  }}
                >
                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: 10,
                    }}
                  >
                    <View style={{ flex: 1, paddingRight: 12 }}>
                      <Text
                        style={{
                          fontSize: 17,
                          lineHeight: 22,
                          fontWeight: '700',
                          color: '#101828',
                          marginBottom: 2,
                        }}
                      >
                        {plateText}
                      </Text>
                      {!!orderNoText && (
                        <Text
                          style={{
                            fontSize: 11,
                            lineHeight: 16,
                            color: '#98a2b3',
                          }}
                        >
                          订单号: {orderNoText}
                        </Text>
                      )}
                    </View>
                    {isActionDisabled ? (
                      <View style={{ alignItems: 'flex-end', minWidth: 72 }}>
                        <Text
                          style={{
                            fontSize: 10.5,
                            color: '#98a2b3',
                            marginBottom: 1,
                          }}
                        >
                          当前状态
                        </Text>
                        <Text
                          style={{
                            color: toneStyle.textColor,
                            fontWeight: '700',
                            fontSize: 14,
                          }}
                        >
                          {badgeText}
                        </Text>
                      </View>
                    ) : (
                      <TouchableOpacity
                        style={{
                          paddingHorizontal: 12,
                          paddingVertical: 7,
                          minWidth: 88,
                          minHeight: 32,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isSyncing ? '#f0f7ff' : '#eef6ff',
                          borderRadius: 16,
                          borderWidth: 1,
                          borderColor: '#98c8f5',
                          opacity: isSyncing ? 0.85 : 1,
                        }}
                        disabled={isSyncing}
                        onPress={() =>
                          activeTab === 'toZhiche'
                            ? syncToZhiche(order)
                            : syncToErp(order)
                        }
                      >
                        {isSyncing ? (
                          <ActivityIndicator size="small" color="#2e90fa" />
                        ) : (
                          <Text
                            style={{
                              color: '#2e90fa',
                              fontWeight: '700',
                              fontSize: 12.5,
                            }}
                          >
                            {actionText}
                          </Text>
                        )}
                      </TouchableOpacity>
                    )}
                  </View>

                  <View
                    style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: 10,
                      paddingHorizontal: 10,
                      paddingVertical: 8,
                      marginBottom: 10,
                    }}
                  >
                    {renderInfoRow('车型', vehicleText)}
                    {renderInfoRow('客户', customerText || '-', {
                      marginBottom: 0,
                    })}
                  </View>

                  <View
                    style={{
                      borderTopWidth: 1,
                      borderTopColor: '#f2f4f7',
                      paddingTop: 10,
                    }}
                  >
                    <Text
                      style={{
                        fontSize: 11.5,
                        fontWeight: '700',
                        color: '#667085',
                        marginBottom: 8,
                      }}
                    >
                      同步信息
                    </Text>
                    {renderInfoRow('状态说明', stateText, {
                      valueColor: stateColor,
                      emphasize: true,
                    })}

                    {activeTab === 'toZhiche' && (
                      <>
                        {renderInfoRow(
                          `${providerName}顾问`,
                          `${employeeDiagnostics?.erpServiceAgentName || '-'}${
                            employeeDiagnostics?.erpServiceAgentMobile
                              ? ` / ${employeeDiagnostics.erpServiceAgentMobile}`
                              : ''
                          }`,
                        )}
                        {employeeDiagnostics?.erpServiceAgentRawValue &&
                          employeeDiagnostics.erpServiceAgentRawValue !==
                            employeeDiagnostics?.erpServiceAgentName &&
                          renderInfoRow(
                            `${providerName}原始值`,
                            employeeDiagnostics.erpServiceAgentRawValue,
                          )}
                        {renderInfoRow(
                          `${providerName}员工ID`,
                          employeeDiagnostics?.erpEmployeeId || '-',
                        )}
                        {renderInfoRow(
                          '员工绑定',
                          employeeDiagnostics?.mapping
                            ? `已绑定 -> ${
                                employeeDiagnostics.mapping.memberName ||
                                employeeDiagnostics.mapping.memberId
                              }`
                            : '未绑定',
                          {
                            valueColor: employeeDiagnostics?.mapping
                              ? StatusColors.Success
                              : StatusColors.Danger,
                            emphasize: true,
                          },
                        )}
                        {employeeDiagnostics?.mapping &&
                          renderInfoRow(
                            '知车手机号',
                            employeeDiagnostics.mapping.memberMobile || '-',
                          )}
                        {renderInfoRow(
                          '同步检查',
                          employeeDiagnostics?.blockReason || '通过',
                          {
                            valueColor: employeeDiagnostics?.blockReason
                              ? StatusColors.Danger
                              : StatusColors.Success,
                            emphasize: true,
                          },
                        )}
                      </>
                    )}

                    {activeTab === 'toErp' && (
                      <>
                        {renderInfoRow(
                          '知车顾问',
                          outboundEmployeeDiagnostics?.zhicheServiceAgentName ||
                            '-',
                        )}
                        {renderInfoRow(
                          '知车员工ID',
                          outboundEmployeeDiagnostics?.zhicheServiceAgentId ||
                            '-',
                        )}
                        {renderInfoRow(
                          '员工绑定',
                          outboundEmployeeDiagnostics?.mapping
                            ? `已绑定 -> ${
                                outboundEmployeeDiagnostics.erpEmployeeName ||
                                outboundEmployeeDiagnostics.erpEmployeeId
                              }`
                            : '未绑定',
                          {
                            valueColor: outboundEmployeeDiagnostics?.mapping
                              ? StatusColors.Success
                              : StatusColors.Danger,
                            emphasize: true,
                          },
                        )}
                        {outboundEmployeeDiagnostics?.mapping &&
                          renderInfoRow(
                            `${providerName}员工`,
                            `${
                              outboundEmployeeDiagnostics.erpEmployeeName ||
                              outboundEmployeeDiagnostics.erpEmployeeId ||
                              '-'
                            }${
                              outboundEmployeeDiagnostics.erpEmployeeId
                                ? ` / ${outboundEmployeeDiagnostics.erpEmployeeId}`
                                : ''
                            }${
                              outboundEmployeeDiagnostics.erpEmployeeMobile
                                ? ` / ${outboundEmployeeDiagnostics.erpEmployeeMobile}`
                                : ''
                            }`,
                          )}
                        {renderInfoRow(
                          '同步检查',
                          outboundEmployeeDiagnostics?.blockReason || '通过',
                          {
                            valueColor: outboundEmployeeDiagnostics?.blockReason
                              ? StatusColors.Danger
                              : StatusColors.Success,
                            emphasize: true,
                          },
                        )}
                      </>
                    )}

                    {!!order.estimatedFinishTime &&
                      renderInfoRow(
                        '预计完工',
                        dayjs(order.estimatedFinishTime).format(
                          'YYYY-MM-DD HH:mm',
                        ),
                        { marginBottom: 0 },
                      )}
                  </View>

                  {extraInfoEntries.length > 0 && (
                    <View
                      style={{
                        marginTop: 10,
                        borderTopWidth: 1,
                        borderTopColor: '#f2f4f7',
                        paddingTop: 8,
                      }}
                    >
                      <TouchableOpacity
                        style={{
                          flexDirection: 'row',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          paddingVertical: 2,
                        }}
                        onPress={() => {
                          const currentKey = String(orderId || index);
                          setExpandedOrderIds(prev => {
                            const next = new Set(prev);
                            if (next.has(currentKey)) {
                              next.delete(currentKey);
                            } else {
                              next.add(currentKey);
                            }
                            saveTabSnapshot(activeTab, {
                              expandedOrderIds: next,
                            });
                            return next;
                          });
                        }}
                      >
                        <Text
                          style={{
                            fontSize: 11.5,
                            fontWeight: '700',
                            color: '#667085',
                          }}
                        >
                          更多信息
                        </Text>
                        <Text
                          style={{
                            fontSize: 11.5,
                            color: '#2e90fa',
                            fontWeight: '600',
                          }}
                        >
                          {isExpanded
                            ? '收起'
                            : `展开 ${extraInfoEntries.length} 项`}
                        </Text>
                      </TouchableOpacity>

                      {isExpanded && (
                        <View
                          style={{
                            backgroundColor: '#fcfcfd',
                            borderRadius: 10,
                            paddingHorizontal: 10,
                            paddingTop: 8,
                            paddingBottom: 4,
                            marginTop: 8,
                          }}
                        >
                          {extraInfoEntries.map((item, extraIndex) =>
                            renderInfoRow(item.label, item.value, {
                              marginBottom:
                                extraIndex === extraInfoEntries.length - 1
                                  ? 0
                                  : 6,
                            }),
                          )}
                        </View>
                      )}
                    </View>
                  )}

                  {latestSyncStatus?.result === ErpSyncResult.Failed && (
                    <View
                      style={{
                        marginTop: 10,
                        backgroundColor: '#fff1f3',
                        borderRadius: 10,
                        paddingHorizontal: 10,
                        paddingVertical: 8,
                      }}
                    >
                      <Text
                        style={{
                          fontSize: 11.5,
                          lineHeight: 17,
                          color: StatusColors.Danger,
                        }}
                      >
                        最近失败: {latestSyncStatus.error || '未知错误'}
                      </Text>
                    </View>
                  )}
                </View>
              );
            })
          )}
        </View>
      </ScrollView>
      <DateTimePicker
        isVisible={showPicker}
        mode="date"
        date={datePickerType === 'start' ? startDate : endDate}
        onConfirm={handleDateConfirm}
        onCancel={hideDateTimePicker}
        title={datePickerType === 'start' ? '选择开始时间' : '选择结束时间'}
        confirmTextIOS="确认"
        cancelTextIOS="取消"
      />
    </View>
  );
});

const SummaryChip = memo(
  ({
    label,
    value,
    tone,
  }: {
    label: string;
    value: number;
    tone: 'pending' | 'success' | 'danger';
  }) => {
    // The original tints were a shade off the page's own #f7f7f7 and read as
    // no card at all, so each tone now carries a fill saturated enough to
    // separate from the page plus a deeper border of the same hue.
    const palette =
      tone === 'success'
        ? { bg: '#e8f8e2', border: '#b7eb8f', color: StatusColors.Success }
        : tone === 'danger'
        ? { bg: '#ffe7e3', border: '#ffbdb5', color: StatusColors.Danger }
        : { bg: '#eef1f6', border: '#dbe2ea', color: Colors.Gray2 };
    return (
      <View
        style={{
          width: '48%',
          backgroundColor: palette.bg,
          borderRadius: 12,
          borderWidth: 1,
          borderColor: palette.border,
          paddingHorizontal: 12,
          paddingVertical: 10,
          marginBottom: 8,
          shadowColor: '#101828',
          shadowOpacity: 0.04,
          shadowRadius: 8,
          shadowOffset: { width: 0, height: 2 },
          elevation: 1,
        }}
      >
        <Text style={{ fontSize: 12, color: '#667085', marginBottom: 4 }}>
          {label}
        </Text>
        <Text style={{ fontSize: 18, color: palette.color, fontWeight: '700' }}>
          {value}
        </Text>
      </View>
    );
  },
);
